import {
  AdminInput,
  AdminSelect,
  AdminSwitch,
  Box,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  Modal,
  PropsModal,
  SuccessToast,
  createBrand,
  lightTheme,
  updateBrandById,
  updateBrandGoogleBusinessUrl,
  googleBusinessUrlChange,
  GOOGLE_BUSINESS_URL_TOOLTIP,
  reportError,
} from '@vgrubs/components';
import { Brand, BrandType } from 'components/src/types/brand.types';
import { LocationEntity } from 'components/src/types/location.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { BrandImageInput } from './BrandImageInput';

const brandProvidersOptions = [
  { name: 'Restaurant Brands', value: BrandType.RB },
  { name: 'TODC', value: BrandType.TDOC },
  { name: 'VPC', value: BrandType.VPC },
  { name: 'VDC', value: BrandType.VDC },
  { name: 'FJ', value: BrandType.FJ },
  { name: 'The Licking Brand', value: BrandType.TLB },
];

const typeOptions = (
  <optgroup label="Select Brand Provider">
    {brandProvidersOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

export const BrandAddModal = ({
  modalIsOpen,
  closeModal,
  setLoading,
  onAddBrandSuccess,
  setData,
  data,
  type = 'add',
  isPos = false,
}: PropsModal & {
  modalIsOpen: boolean;
  onAddBrandSuccess?: (data: Brand) => void;
  setData:
    | Dispatch<SetStateAction<LocationEntity | null>>
    | Dispatch<SetStateAction<Brand | null>>;
  data: LocationEntity | Brand | null;
  type: 'add' | 'edit';
  isPos?: boolean;
}) => {
  const [brandName, setBrandName] = useState('');

  const [toggle, setToggle] = useState(false);
  const [brandProvider, setBrandProvider] = useState(
    brandProvidersOptions[0].value
  );
  const [image, setImage] = useState<string | File | Blob | null>('');
  const [vOrdersOrderingUrl, setvOrdersOrderingUrl] = useState('');
  const [googleBusinessUrl, setGoogleBusinessUrl] = useState('');
  const [pushOrdersToPos, setPushOrdersToPos] = useState(false);

  const isBrandModal = data && 'type' in data;

  const isVGrubsBrand = isBrandModal && data.type === BrandType.vGrubs;

  const isEdit = type === 'edit';
  const isAdd = type === 'add';
  const showPushOrdersToPos = isEdit && isPos && isBrandModal;
  /* Only a virtual brand has a link of its own: orders from a location's own
     brand use the location's (Edit Location). */
  const showGoogleBusinessUrl = isEdit && isBrandModal && !data.isLocation;

  useEffect(() => {
    if (data && isEdit) {
      setBrandName(data.name);
      setvOrdersOrderingUrl(data.vOrdersOrderingUrl ?? '');
      if (isBrandModal) setGoogleBusinessUrl(data.googleBusinessUrl ?? '');
      if (isVGrubsBrand) {
        setToggle(true);
      } else {
        setToggle(false);
        if (isBrandModal) {
          setBrandProvider(data.type);
        }
      }
      if (isBrandModal) {
        setImage(data.imageUrl);
        setPushOrdersToPos(data.pushOrdersToPos ?? false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);

  async function handleAddEdit() {
    if (!data) return;

    const google = showGoogleBusinessUrl
      ? googleBusinessUrlChange(
          (data as Brand).googleBusinessUrl,
          googleBusinessUrl
        )
      : ({ kind: 'unchanged' } as const);
    if (google.kind === 'invalid') {
      return ErrorToast('The Google review link must start with https://');
    }

    const brand = {
      name: brandName as string | undefined,
      locationId: data?.id as number | undefined,
      type: (toggle ? 'vGrubs' : brandProvider) as BrandType | undefined,
      vOrdersOrderingUrl,
      pushOrdersToPos: showPushOrdersToPos ? pushOrdersToPos : undefined,
    };

    if (isEdit) delete brand.locationId;

    const formData = new FormData();
    if (typeof image !== 'string' && image !== null) {
      formData.append('image', image);
    }

    if (isVGrubsBrand && type !== 'add') {
      delete brand.name;
      delete brand.type;
      formData.delete('image');
    }

    formData.append('brand', JSON.stringify(brand));
    try {
      setLoading?.(true);

      if (isAdd) {
        const { data } = await createBrand(formData);

        setData((prev) => {
          return { ...prev, brands: [data.data, ...prev.brands] };
        });
        onAddBrandSuccess?.(data.data);
        setBrandName('');
        setBrandProvider(brandProvidersOptions[0].value);
        setImage('');
      } else {
        let res = await updateBrandById(data.id, formData);
        // Its own route, which answers with the brand as it now stands.
        if (google.kind === 'set') {
          res = await updateBrandGoogleBusinessUrl(data.id, google.value);
        }

        (setData as Dispatch<SetStateAction<Brand | null>>)(res.data.data);
        SuccessToast(res.data.message);
        closeModal?.();
      }

      setLoading?.(false);
    } catch (error) {
      reportError(error);
      setLoading?.(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!brandName) return ErrorToast('Please enter Brand Name');

    handleAddEdit();
  }

  return (
    <Modal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`${isEdit ? 'Edit' : 'Add'} Brand`}
    >
      <form onSubmit={handleSubmit}>
        <Div $flex $justifyCenter>
          <BrandImageInput
            image={image}
            onChange={(img) => setImage(img)}
            disabled={!!isVGrubsBrand}
          />
        </Div>

        <Div $mb="24">
          <AdminInput
            label="Brand Name"
            placeholder="Enter here"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            disabled={!!isVGrubsBrand}
            $textCenter
          />
        </Div>

        {!toggle && (
          <AdminSelect
            value={brandProvider}
            options={typeOptions}
            onChange={({ target }) =>
              setBrandProvider(target.value as BrandType)
            }
            label="Choose Brand Provider"
          />
        )}

        <Div $mt="24">
          <AdminInput
            label="vOrders Direct Ordering URL"
            placeholder="Enter here"
            value={vOrdersOrderingUrl}
            onChange={(e) => setvOrdersOrderingUrl(e.target.value)}
            $textCenter
            tooltip="This link is used for vOrders. When customers opt in on the vDrive tracking page, they will receive this direct ordering link via SMS."
            tooltipWidth="300px"
          />
        </Div>

        {showGoogleBusinessUrl && (
          <Div $mt="24">
            <AdminInput
              label="Google Review Link"
              placeholder="https://g.page/r/..."
              value={googleBusinessUrl}
              onChange={(e) => setGoogleBusinessUrl(e.target.value)}
              $textCenter
              tooltip={GOOGLE_BUSINESS_URL_TOOLTIP}
              tooltipWidth="300px"
            />
          </Div>
        )}

        {showPushOrdersToPos && (
          <Box $mt="24" $flex $alignCenter $between>
            <div>
              <H5 $left $mb="0">
                Push Orders to POS
              </H5>
              <Disclaimer $left $color={lightTheme.lightGray} $mb="0">
                Forward incoming orders for this brand to the POS.
              </Disclaimer>
            </div>
            <Div>
              <AdminSwitch
                checked={pushOrdersToPos}
                onChange={(v) => setPushOrdersToPos(v.target.checked)}
              />
            </Div>
          </Box>
        )}

        <Button $selected $w="100%" $mt="50" $mtSM="30" type="submit">
          Submit
        </Button>
      </form>
    </Modal>
  );
};
